import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { TabContent, TabPane, Nav, NavItem, NavLink, } from 'reactstrap';
import { useToggle, useInterval, useTitle, } from 'react-use';
import classnames from 'classnames';
import { get } from 'lodash';
import retry from 'async-retry';
import qs from 'qs';

import stagingVersion from '../staging_version.json';
import productionVersion from '../production_version.json';

import env from '../env';
import logo from '../logo.jpg';
import logo2 from '../logo2.jpg';
import logo3 from '../logo3.jpg';
import logo4 from '../logo4.jpg';
import logo5 from '../logo5.png';
import logo6 from '../logo6.png';
import logo7 from '../logo7.png';
import logo8 from '../logo8.jpg';
import firebase, { functions } from '../firebase';
import useFirebaseUser from './hooks/useFirebaseUser';
import useDocumentSubscription from './hooks/useDocumentSubscription';
import PleaseConfirmEmail from './PleaseConfirmEmail';
import SignInForm from './forms/SignInForm';
import SignUpForm from './forms/SignUpForm';

const _logo = ({ 1: logo, 2: logo2, 3: logo3, 4: logo4, 5: logo5, 6: logo6, 7: logo7, 8: logo8, 9: logo, })[env('NUMBER') || '1'];
const getLastTenantId = functions.httpsCallable('getLastTenantId');
const auth = firebase.auth();
const db = firebase.firestore();
const usersRef = db.collection('users');
const tenantsRef = db.collection('tenants');
const publicDataRef = db.collection('publicData');
const appVersion = ({
  staging: stagingVersion,
  production: productionVersion,
})[process.env.REACT_APP_ENV || 'development'];

export default function Root (props) {
  const { pathname } = window.location;
  const queryParams = qs.parse(window.location.search.slice(1));
  const isForStaff = !!pathname.match(/^\/staff/) || window.location.search.includes('staff=1');
  const isLoginSpApi = !!pathname.match(/^\/loginSpApi/);
  const signUpTagId = queryParams.tag;
  const remoteAppVersion = useDocumentSubscription(publicDataRef.doc('appVersion'));
  const { firebaseUser, emailVerified, reloadEmailVerified } = useFirebaseUser();
  const [showsLoginForm, toggleLoginForm] = useToggle(false);
  const [tab, setTab] = useState(queryParams.signup === '1' ? 'signUp' : 'signIn');
  const user = useDocumentSubscription(firebaseUser && usersRef.doc(firebaseUser.uid), [firebaseUser]);
  const isOldAppVersion = remoteAppVersion?.value > appVersion;
  useTitle(env('SERVICE_NAME') || 'ロジスピ');
  useEffect(() => {
    if (firebaseUser != null) {
      toggleLoginForm(false);
    } else if (firebaseUser === null) {
      toggleLoginForm(true);
    }
  }, [firebaseUser]);
  useEffect(() => {
    if(appVersion != null && (remoteAppVersion == null || remoteAppVersion.value < appVersion) && user?.admin) {
      publicDataRef.doc('appVersion').set({ value: appVersion, updatedAt: new Date(), });
    }
  }, [user, remoteAppVersion]);
  useInterval(() => {
    reloadEmailVerified();
  }, (firebaseUser && !emailVerified && !get(user, 'isStaff')) ? 3000 : null);

  const onSubmitSignInForm = async (values) => {
    const { password } = values;
    try {
      await auth.signInWithEmailAndPassword(isForStaff ? `${values.staffId}@wizzlinx.com` : values.email, password);
    } catch(e) {
      console.error(e);
      const message = ({
        'auth/invalid-email': `${isForStaff ? 'スタッフID' : 'メールアドレス'}の形式が正しくありません`,
        'auth/user-not-found': 'ユーザーが存在しません',
      })[e.code] || 'ログインに失敗しました';
      toast.error(message);
    }
  };
  const onSubmitSignUpForm = async (values) => {
    const { email, password, displayName, phone, tenantName, } = values;
    try {
      // NOTE: firestoreの活を確認
      await db.collection('publicData').doc('dummy').get();

      // NOTE: 登録URLのタグIDをそのまま付与する（未存在のIDは表示時に無視される）
      const tagIds = signUpTagId ? [signUpTagId] : [];

      let firebaseUser;
      try {
        const { data: { lastId } } = await getLastTenantId();
        const { user: _user, user: { uid } } = await auth.createUserWithEmailAndPassword(email, password);
        await retry(async () => {
          firebaseUser = _user;
          const tenantId = env('TENANT_PREFIX') + (lastId != null ? parseInt(lastId.replace(new RegExp(`^${env('TENANT_PREFIX')}`), ''), 10) + 1 : 1);
          const tenantRef = tenantsRef.doc(tenantId);
          const batch = db.batch();
          batch.set(tenantRef, { name: tenantName, createdAt: new Date(), createsShippingRequestWithUnmappedItems: false, ...(tagIds.length > 0 && { tagIds }), });
          batch.set(usersRef.doc(uid), {
            uid,
            email,
            displayName,
            phone,
            tenantId: tenantRef.id,
          });
          await batch.commit();
          await firebaseUser.sendEmailVerification({ url: window.location.href, });
          try { await auth.currentUser.updateProfile({ displayName }); } catch(e) { }
          toast.success('ご登録のメールアドレスに確認メールをお送りしました');
        }, { retries: 3 });
      } catch(e) {
        await retry(async () => {
          await firebaseUser?.delete();
        }, { retries: 3 });
        throw e;
      }
    } catch(e) {
      console.error(e);
      const message = ({
        'auth/invalid-email': 'メールアドレスの形式が正しくありません',
        'auth/weak-password': 'パスワードは最低6文字以上で指定してください',
      })[e.code] || '登録に失敗しました';
      toast.error(message);
    }
  };

  return (
    <div>
      {
        isLoginSpApi ? (
          <BrowserRouter>
            {props.routes({ user, firebaseUser, isOldAppVersion, })}
          </BrowserRouter>
        ) : showsLoginForm ? (
          <div className="container py-5">
            <div className="row">
              <div className="col-md-8 offset-md-2 col-lg-6 offset-lg-3">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-center mb-3">
                      <img src={_logo} style={{ height: 36 }} />
                    </div>
                    {
                      !isForStaff && (
                        <Nav tabs>
                          <NavItem>
                            <NavLink className={classnames('cursor-pointer', { active: tab === 'signIn' })} onClick={_ => setTab('signIn')}>
                              ログイン
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink className={classnames('cursor-pointer', { active: tab === 'signUp' })} onClick={_ => setTab('signUp')}>
                              ユーザー登録
                            </NavLink>
                          </NavItem>
                        </Nav>
                      )
                    }
                    <TabContent activeTab={tab}>
                      <TabPane tabId="signIn" className="pt-3">
                        <SignInForm onSubmit={onSubmitSignInForm} isForStaff={isForStaff} />
                      </TabPane>
                      <TabPane tabId="signUp" className="pt-3">
                        <SignUpForm onSubmit={onSubmitSignUpForm} />
                      </TabPane>
                    </TabContent>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          firebaseUser != null && (
            (emailVerified || get(user, 'isStaff')) ? (
              user != null && (
                <BrowserRouter>
                  {props.routes({ user, firebaseUser, isOldAppVersion, })}
                </BrowserRouter>
              )
            ) : (
              <PleaseConfirmEmail firebaseUser={firebaseUser} />
            )
          )
        )
      }
      <ToastContainer className="text-break" />
    </div>
  );
};
